import { ArenaOpsUsdtSchema } from "./arena-ops-filters.dto";
import { z } from "zod";

/**
 * T1 — the funnel. `expired` is `effectiveStatus`, resolved inside the tickets
 * module: `ArenaTicketStatus.EXPIRED` is never persisted, so raw `status` can
 * never answer it. `faceSpentUsdt` is `USE − RETURN`.
 *
 * Two identities hold, both server-computed so the panel never does money
 * arithmetic to reach a headline figure:
 *
 *   granted        = active + used + expired
 *   faceIssuedUsdt = faceSpentUsdt + faceHeldUsdt + faceExpiredUsdt
 *
 * The second one is a consequence of the ticket state machine, not a
 * coincidence: a ticket is USED exactly when its `USE − RETURN` nets to 1 and
 * ACTIVE when it nets to 0, so `faceSpentUsdt` IS the face value of the
 * currently-used tickets.
 *
 * `returnEvents` is outside both. It counts `RETURN` movements, not tickets —
 * one ticket can be granted → used → returned → used again — so adding it to
 * either partition double-counts. It is a line beside the funnel, never a slice
 * of it.
 */
/**
 * The funnel per grant source, counts summed over denominations so
 * `sum(bySource.granted) = granted`. `medianSecondsToUse` is grant → surviving
 * USE over the source's currently-used tickets; `null` when none is used.
 */
export const ArenaOpsTicketsLifecycleBySourceRowSchema = z.object({
  source: z.string(),
  granted: z.number().int(),
  active: z.number().int(),
  used: z.number().int(),
  expired: z.number().int(),
  medianSecondsToUse: z.number().nonnegative().nullable(),
});
export type ArenaOpsTicketsLifecycleBySourceRow = z.infer<typeof ArenaOpsTicketsLifecycleBySourceRowSchema>;

export const ArenaOpsTicketsLifecycleResponseSchema = z.object({
  granted: z.number().int(),
  active: z.number().int(),
  used: z.number().int(),
  expired: z.number().int(),
  /** RETURN movements, NOT tickets. Never a slice of the funnel — see above. */
  returnEvents: z.number().int(),
  faceIssuedUsdt: ArenaOpsUsdtSchema,
  faceSpentUsdt: ArenaOpsUsdtSchema,
  faceHeldUsdt: ArenaOpsUsdtSchema,
  /** The tab's headline: 741.00 today, 64% of everything ever issued. */
  faceExpiredUsdt: ArenaOpsUsdtSchema,
  bySource: z.array(ArenaOpsTicketsLifecycleBySourceRowSchema),
});
export type ArenaOpsTicketsLifecycleResponse = z.infer<typeof ArenaOpsTicketsLifecycleResponseSchema>;
